import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiShield } from 'react-icons/fi';
import { RiAdminLine } from 'react-icons/ri';

const colors = {
  orange: '#f35018',
  orangeDark: '#c73a09',
  blue: '#2f3194',
  cyan: '#63decc',
  glassWhite: 'rgba(255, 255, 255, 0.85)',
  textMain: '#0f1137',
  bgLight: '#f4f6fc',
  cardBg: '#ffffff',
  muted: '#64748b',
  border: '#e2e8f0',
};

const BASE_URL = 'https://rvapi.emedha.in/auth/api';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!email.trim() || !password.trim()) {
      setErrorMsg('Please enter both email and password.');
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/api/admin/login`, {
        email: email.trim().toLowerCase(),
        password: password.trim(),
      }).catch(() => {
        return axios.post(`${BASE_URL}/admin/login`, {
          email: email.trim().toLowerCase(),
          password: password.trim(),
        });
      });

      if (res.data?.success) {
        sessionStorage.setItem('admin_user', JSON.stringify({ email: res.data.email }));
        navigate('/admin/dashboard');
      } else {
        setErrorMsg(res.data?.message || 'Access denied.');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Invalid credentials or server error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.pageWrapper}>
      {/* Background Glowing Ambient Orbs */}
      <div style={styles.orbTopRight} />
      <div style={styles.orbBottomLeft} />

      <div style={styles.cardContainer}>
        {/* Top Floating Security Badge */}
        <div style={styles.badgeRow}>
          <div style={styles.securityBadge}>
            <FiShield size={12} color={colors.cyan} style={{ marginRight: '6px' }} />
            <span style={styles.securityBadgeText}>ADMIN ACCESS</span>
          </div>
        </div>

        {/* Brand Header */}
        <div style={styles.brandHeader}>
          <div style={styles.logoHalo}>
            <RiAdminLine size={32} color={colors.cyan} />
          </div>
          <h1 style={styles.brandTitle}>
            Quizify<span style={{ color: colors.orange }}>AI</span>
          </h1>
      
        </div>

        {/* Error Alert Banner */}
        {errorMsg ? (
          <div style={styles.alertBanner}>
            <span>{errorMsg}</span>
          </div>
        ) : null}

        {/* Login Form */}
        <form onSubmit={handleLogin} style={styles.form}>
          {/* Email Input */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>ADMIN EMAIL</label>
            <div style={styles.inputWrapper}>
              <FiMail size={18} color={colors.blue} style={styles.fieldIcon} />
              <input
                type="email"
                placeholder="admin@quizifyai.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.inputField}
                required
              />
            </div>
          </div>

          {/* Password Input */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>PASSWORD</label>
            <div style={styles.inputWrapper}>
              <FiLock size={18} color={colors.blue} style={styles.fieldIcon} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.inputField}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeBtn}
              >
                {showPassword ? <FiEyeOff size={18} color={colors.muted} /> : <FiEye size={18} color={colors.muted} />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitBtn,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? (
              <span>Verifying Credentials...</span>
            ) : (
              <>
                <span>Authorize & Enter</span>
                <FiArrowRight size={18} />
              </>
            )}
          </button>
        </form>

        {/* <div style={styles.footerNote}>
          <FiLock size={12} color="rgba(255, 255, 255, 0.6)" style={{ marginRight: '6px' }} />
          <span>Internal Organization Systems • Strictly Monitored</span>
        </div> */}
      </div>
    </div>
  );
}

const styles = {
  pageWrapper: {
    minHeight: '100vh',
    width: '100%',
    backgroundColor: colors.blue,
    backgroundImage: 'radial-gradient(circle at 50% 20%, rgba(99, 222, 204, 0.12) 0%, #2f3194 70%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    boxSizing: 'border-box',
  },
  orbTopRight: {
    position: 'absolute',
    top: '-80px',
    right: '-80px',
    width: '320px',
    height: '320px',
    borderRadius: '50%',
    backgroundColor: 'rgba(99, 222, 204, 0.25)',
    filter: 'blur(70px)',
    pointerEvents: 'none',
  },
  orbBottomLeft: {
    position: 'absolute',
    bottom: '-100px',
    left: '-100px',
    width: '380px',
    height: '380px',
    borderRadius: '50%',
    backgroundColor: 'rgba(243, 80, 24, 0.2)',
    filter: 'blur(80px)',
    pointerEvents: 'none',
  },
  cardContainer: {
    width: '100%',
    maxWidth: '440px',
    backgroundColor: colors.cardBg,
    borderRadius: '26px',
    padding: '36px 32px',
    boxShadow: '0 24px 60px rgba(15, 17, 55, 0.35)',
    border: `1px solid ${colors.border}`,
    position: 'relative',
    zIndex: 10,
    boxSizing: 'border-box',
  },
  badgeRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  securityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#0f1137',
    padding: '6px 14px',
    borderRadius: '30px',
    border: '1px solid rgba(99, 222, 204, 0.3)',
  },
  securityBadgeText: {
    color: colors.cyan,
    fontSize: '10px',
    fontWeight: '800',
    letterSpacing: '1px',
  },
  brandHeader: {
    textAlign: 'center',
    marginBottom: '28px',
  },
  logoHalo: {
    width: '64px',
    height: '64px',
    borderRadius: '20px',
    backgroundColor: colors.blue,
    margin: '0 auto 14px auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 10px 24px rgba(47, 49, 148, 0.35)',
  },
  brandTitle: {
    fontSize: '28px',
    fontWeight: '900',
    color: colors.textMain,
    margin: '0 0 6px 0',
    letterSpacing: '-0.5px',
  },
  brandSubtitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: colors.muted,
    margin: 0,
  },
  alertBanner: {
    backgroundColor: '#fee2e2',
    color: '#b91c1c',
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: '700',
    marginBottom: '20px',
    textAlign: 'center',
    border: '1px solid #fecaca',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
  },
  label: {
    fontSize: '11px',
    fontWeight: '800',
    color: colors.muted,
    letterSpacing: '0.8px',
    marginBottom: '6px',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: colors.bgLight,
    border: `1.5px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '0 14px',
    height: '50px',
    transition: 'border-color 0.2s',
  },
  fieldIcon: {
    marginRight: '10px',
    flexShrink: 0,
  },
  inputField: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.textMain,
  },
  eyeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  },
  submitBtn: {
    backgroundColor: colors.orange,
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    height: '50px',
    fontSize: '15px',
    fontWeight: '800',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '6px',
    boxShadow: '0 8px 20px rgba(243, 80, 24, 0.35)',
    transition: 'all 0.2s ease',
  },
  footerNote: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '24px',
    fontSize: '11px',
    color: colors.muted,
    fontWeight: '600',
  },
};